import React from 'react';

import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  BorderColor as BorderColorIcon,
} from '@mui/icons-material';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import { PeoPayrollProviderStaffInfoFragment } from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { Table, TableColumn, TableFilter, TableRowAction, TableThemes } from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { useSearch } from 'src/hooks/common';
import { useModal, useModalV2 } from 'src/hooks/shared';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { MfaManagementModal } from 'src/modals/auth';
import { PaginationContextValue } from 'src/providers/PaginationProvider';

export interface PeoPayrollProviderStaffTableProps {
  peoPayrollProviderId: string | undefined;
  peoPayrollProviderStaffList?: PeoPayrollProviderStaffInfoFragment[];
  peoPayrollProviderStaffCount?: number;
  pagination: PaginationContextValue;
  onRefresh: () => Promise<void>;
  loading: boolean;
}

export const PeoPayrollProviderStaffTable: React.FC<PeoPayrollProviderStaffTableProps> = ({
  peoPayrollProviderId,
  peoPayrollProviderStaffList = [],
  peoPayrollProviderStaffCount = 0,
  pagination,
  onRefresh,
  loading,
}) => {
  const { setSearchValue, searchValue } = useSearch();
  const { openModal } = useModal();
  const { openModal: openModalV2 } = useModalV2();

  const { hasPlatformRoles } = useCurrentUserPermissions();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const rowActions = React.useMemo(() => {
    const actions: Array<TableRowAction<PeoPayrollProviderStaffInfoFragment>> = [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: (staff: PeoPayrollProviderStaffInfoFragment) => {
          openModal(ModalId.PeoPayrollProviderStaffDetailsModal, {
            peoPayrollProviderStaffId: staff.id,
            peoPayrollProviderId,
          });
        },
      },
    ];

    if (hasPlatformRoles) {
      actions.push({
        startIcon: <AdminPanelSettingsIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'MFA Management',
        onAction: staff => {
          openModalV2(MfaManagementModal, { id: ModalId.MfaManagement, userId: staff.user.id });
        },
      });
    }

    return actions;
  }, [hasPlatformRoles, openModal, openModalV2, peoPayrollProviderId]);

  return (
    <Table
      dataSource={peoPayrollProviderStaffList}
      rowCount={peoPayrollProviderStaffCount}
      columns={COLUMNS}
      pagination={pagination}
      onSearch={setSearchValue}
      loading={loading}
      rowActions={rowActions}
      filters={FILTERS}
      tableTheme={TableThemes.withBorder}
      onRefresh={onRefresh}
    />
  );
};

const COLUMNS: TableColumn<PeoPayrollProviderStaffInfoFragment>[] = [
  {
    title: 'Full Name',
    key: 'user.firstName',
    sortable: true,
    render: (_valueByDataPath, peoProviderStaff, { rowActions }) => {
      return (
        <ClickableLabel
          type="button"
          onClick={() => rowActions?.[0]?.onAction(peoProviderStaff)}
          label={peoProviderStaff.user.fullName}
          logoUrl={peoProviderStaff.user.avatar?.downloadUrl}
        />
      );
    },
  },
  {
    title: 'Phone Number',
    key: 'user.phoneNumber',
    render: (_valueByDataPath, peoProviderStaff) => (
      <DemoPrivacyText>
        {parseNullableValue(formatPhoneNumber(peoProviderStaff.user.phoneNumber))}
      </DemoPrivacyText>
    ),
  },
  {
    title: 'Email',
    key: 'user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, peoProviderStaff) => (
      <DemoPrivacyText>{parseNullableValue(peoProviderStaff.user.email)}</DemoPrivacyText>
    ),
  },
  {
    title: 'Status',
    key: 'user.status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, peoProviderStaff) => (
      <UserStatusChip userStatus={peoProviderStaff.user.status} />
    ),
  },
];

const FILTERS: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
